import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { ArrowUpwardRounded } from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";

import { backend_api } from "../../../static/server";
import { useDialog } from "../../DialogContext";
import DrawerTemplate from "../../LawyerCompo/Cases/DrawerTemplate";
import MatterApiField from "../../LawyerCompo/Settings/FieldsUi/MatterApiField";

const DocAiChat = ({ activeAI }) => {
  const { token } = useSelector((state) => state.user);
  const { showDialog } = useDialog();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);
  const [step, setStep] = useState("select-case");

  const messagesEndRef = useRef(null);
  const aiColor = activeAI?.color || "#a8e6cf";

  useEffect(() => {
    const initialMessage = {
      id: "welcome",
      sender: "ai",
      text: "Hello! I'm DocAI, your document drafting assistant. First, please select a matter:",
      timestamp: new Date().toISOString(),
    };

    setMessages([initialMessage]);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleCaseSelect = (caseItem) => {
    if (!caseItem) return;

    setSelectedCase(caseItem);
    setStep("drafting");

    const userConfirmMessage = {
      id: Date.now(),
      sender: "user",
      text: `Selected case: ${caseItem.case_name || `Case #${caseItem.id}`}`,
      timestamp: new Date().toISOString(),
    };

    const aiPromptMessage = {
      id: Date.now() + 1,
      sender: "ai",
      text: `Great! You're working on "${caseItem.case_name}".

Please describe the document you need.

Example:

Title: Legal Notice for Breach of Contract
Purpose: Notify the other party about unpaid dues
Facts: ABC Technologies Pvt Ltd failed to pay invoice #12345
Stage: 1
Format: pdf
Folder: 10
Tags: breach of contract, recovery, legal notice`,
      timestamp: new Date().toISOString(),
    };

    setMessages((prevMessages) => [
      ...prevMessages,
      userConfirmMessage,
      aiPromptMessage,
    ]);
  };

  const handleSend = async () => {
    if (!input.trim() || loading || !selectedCase) return;

    const userMessage = {
      id: Date.now(),
      sender: "user",
      text: input,
      timestamp: new Date().toISOString(),
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${backend_api}ai-employees/docai/draft/`,
        {
          case_id: selectedCase.id,
          input_text: input,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const blocks = response.data?.ai_response?.render_blocks || [];
      const aiMessage = {
        id: Date.now() + 1,
        sender: "ai",
        blocks,
        timestamp: new Date().toISOString(),
      };

      setMessages((prevMessages) => [...prevMessages, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        sender: "ai",
        text: "Sorry, an error occurred while drafting the document.",
        timestamp: new Date().toISOString(),
      };

      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const renderBlocks = (blocks) =>
    blocks.map((block, index) => {
      if (block.type === "heading") {
        return (
          <Typography
            key={index}
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 1 }}
          >
            {block.text}
          </Typography>
        );
      }

      return (
        <Typography key={index} variant="body2" sx={{ fontSize: "0.95rem" }}>
          {block.text}
        </Typography>
      );
    });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: alpha(aiColor, 0.05),
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid #d4c9b6" }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          DocAI · Legal Document Assistant
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              alignSelf: message.sender === "user" ? "flex-end" : "flex-start",
              maxWidth: "85%",
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                borderRadius: "18px",
                bgcolor: message.sender === "user" ? aiColor : "#fff",
              }}
            >
              {message.blocks ? (
                renderBlocks(message.blocks)
              ) : (
                <Typography variant="body2">{message.text}</Typography>
              )}
            </Paper>
            <Typography variant="caption" sx={{ ml: 1 }}>
              {new Date(message.timestamp).toLocaleTimeString()}
            </Typography>
          </Box>
        ))}

        {step === "select-case" && (
          <Box
            sx={{
              mt: 2,
              width: "100%",
              maxWidth: 500,
              alignSelf: "flex-start",
            }}
          >
            <MatterApiField
              name="matter"
              label="Matter"
              value={selectedCase}
              onChange={handleCaseSelect}
              token={token}
              apiUrl={`${backend_api}case/register/`}
              isRequired
              returnFullData
              searchKey="name"
              AddMatterFunc={({ onCreated }) =>
                showDialog(<DrawerTemplate onMatterCreated={onCreated} />, {
                  dialogTitle: "Select Template",
                })
              }
              noDataText="No matters found. Click 'Add Matter' to create one."
              addButtonText="Add Matter"
              placeholder="Search or select a matter"
            />
          </Box>
        )}

        {loading && <CircularProgress size={24} sx={{ color: aiColor }} />}
        <div ref={messagesEndRef} />
      </Box>

      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          handleSend();
        }}
        sx={{ p: 2 }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={5}
          value={input}
          disabled={step !== "drafting"}
          onChange={(event) => setInput(event.target.value)}
          placeholder={
            step === "select-case"
              ? "First, select a matter above"
              : "Describe your document request..."
          }
          InputProps={{
            endAdornment: (
              <IconButton
                type="submit"
                disabled={!input.trim() || step !== "drafting"}
                sx={{ bgcolor: aiColor, color: "#fff" }}
              >
                <ArrowUpwardRounded />
              </IconButton>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default DocAiChat;
